// CTABlockAdmin.jsx - Full API save/load + every style editable (matches modal richness)
import React, { useState, useEffect } from 'react';
import CTABlock from '../components/Cta';
import axios from 'axios';
import configs from '../config/config';

const CTA_PRESETS = {
  'split-urgency': {
    label: 'Split Image + Urgency',
    description: 'Image background, countdown, badges — strong conversion driver',
    defaultConfig: {
      title: 'Weekend MEGA SALE',
      subtitle: 'Up to 70% off fashion — limited time only!',
      background: 'from-purple-600 via-purple-700 to-pink-600',
      textColor: '#ffffff',
      titleStyle: { color: '#ffffff', fontSize: 'text-4xl md:text-5xl', fontWeight: 'font-black', fontFamily: 'font-sans' },
      subtitleStyle: { color: '#ffffff', fontSize: 'text-lg', fontWeight: 'font-normal', fontFamily: 'font-sans' },
      buttonText: 'Shop Now',
      buttonBg: 'bg-white',
      buttonTextColor: '#7c3aed',
      buttonStyle: { fontSize: 'text-lg', fontWeight: 'font-bold', padding: 'py-4 px-8', borderRadius: 'rounded-xl' },
      imageUrl: '',
      showCountdown: true,
      countdownValues: { days: '02', hours: '14', mins: '35' },
      countdownBoxStyle: { bg: 'bg-white/20', textColor: '#ffffff', borderRadius: 'rounded-lg' },
      badgeText: 'Limited Time',
      badgeColor: 'bg-yellow-400 text-gray-900',
      badgeStyle: { fontSize: 'text-sm', padding: 'px-4 py-1.5', borderRadius: 'rounded-full' },
      features: ['Fast delivery', 'Easy returns', 'Member discounts'],
      featureIconColor: '#ffffff',
      featureTextColor: '#ffffff',
      featureBoxStyle: { bg: 'bg-white/20', padding: 'p-1.5', borderRadius: 'rounded-full' },
    },
  },
  'horizontal-banner': {
    label: 'Horizontal Banner',
    description: 'Full-width layout with image + text side-by-side',
    defaultConfig: {
      title: 'Summer Collection 2024',
      subtitle: 'Discover the latest trends — shop before they’re gone!',
      background: 'from-blue-600 via-indigo-600 to-purple-600',
      textColor: '#ffffff',
      titleStyle: { color: '#ffffff', fontSize: 'text-4xl md:text-5xl', fontWeight: 'font-black', fontFamily: 'font-sans' },
      subtitleStyle: { color: '#ffffff', fontSize: 'text-lg', fontWeight: 'font-normal', fontFamily: 'font-sans' },
      buttonText: 'Explore Collection',
      buttonBg: 'bg-white',
      buttonTextColor: '#2563eb',
      buttonStyle: { fontSize: 'text-lg', fontWeight: 'font-bold', padding: 'py-4 px-8', borderRadius: 'rounded-xl' },
      imageUrl: '',
      showSparkleBadge: true,
      sparkleBadgeText: 'Featured',
      sparkleBadgeStyle: { bg: 'bg-white/20', textColor: '#ffffff', padding: 'px-4 py-1.5', borderRadius: 'rounded-full' },
    },
  },
  'dark-vip': {
    label: 'Dark VIP / Membership',
    description: 'Minimalist dark style — great for loyalty / subscription CTAs',
    defaultConfig: {
      title: 'Join Our VIP Club',
      subtitle: 'Exclusive deals, early access, birthday gifts & more',
      background: 'bg-gray-900',
      textColor: '#ffffff',
      titleStyle: { color: '#ffffff', fontSize: 'text-4xl md:text-6xl', fontWeight: 'font-black', fontFamily: 'font-sans' },
      subtitleStyle: { color: '#d1d5db', fontSize: 'text-lg', fontWeight: 'font-normal', fontFamily: 'font-sans' },
      buttonText: 'Sign Up Free – Get 15% Off',
      buttonBg: 'bg-gradient-to-r from-orange-500 to-pink-500',
      buttonTextColor: '#ffffff',
      buttonStyle: { fontSize: 'text-xl', fontWeight: 'font-bold', padding: 'py-5 px-10', borderRadius: 'rounded-xl' },
      showBenefits: true,
      features: [
        { text: 'Birthday Gift', icon: '🎁' },
        { text: 'Early Access', icon: '⚡' },
        { text: 'Extra 10% Off', icon: '💎' },
        { text: 'Free Shipping', icon: '📦' },
      ],
      benefitsBoxStyle: { bg: 'bg-white/10', padding: 'px-5 py-2.5', borderRadius: 'rounded-lg' },
    },
  },
};

const CTABlockAdmin = ({ slug = '', onSave, onClose }) => {
  const [config, setConfig] = useState({
    isVisible: true,
    preset: 'split-urgency',
    ...CTA_PRESETS['split-urgency'].defaultConfig,
    devMode: false,
    customStyles: { container: { padding: 'p-8 md:p-12', margin: 'mx-auto max-w-7xl', borderRadius: 'rounded-2xl', boxShadow: 'shadow-2xl' } },
  });

  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [fullScreenPreview, setFullScreenPreview] = useState(false);

  const token = localStorage.getItem("token");

  useEffect(() => {
    const loadCtaSettings = async () => {
      if (!token) {
        setLoading(false);
        return;
      }

      try {
        setLoading(true);
        await axios.get(configs.API_URL + "/sanctum/csrf-cookie", { withCredentials: true });

        let response;
        const isDuplicate = slug.includes('-');

        if (isDuplicate) {
          response = await axios.get(
            `${configs.API_URL}/api/pullCustomSettings/${slug}`,
            { headers: { Authorization: `Bearer ${token}` }, withCredentials: true }
          );
        } else {
          response = await axios.get(
            configs.API_URL + '/api/pullCtaSettings',
            { headers: { Authorization: `Bearer ${token}` }, withCredentials: true }
          );
        }

        const dbData = isDuplicate
          ? (response.data.customSettings || {})
          : (response.data.ctaSettings || {});

        applyDbData(dbData);
      } catch (error) {
        console.error("Failed to load CTA settings:", error);
      } finally {
        setLoading(false);
      }
    };

    const applyDbData = (dbData) => {
      const presetKey = dbData.preset || 'split-urgency';
      const presetDefaults = CTA_PRESETS[presetKey]?.defaultConfig || {};

      setConfig({
        isVisible: dbData.isVisible ?? true,
        preset: presetKey,
        title: dbData.title || presetDefaults.title || '',
        subtitle: dbData.subtitle || presetDefaults.subtitle || '',
        background: dbData.background || presetDefaults.background || '',
        textColor: dbData.textColor || presetDefaults.textColor || '#ffffff',
        titleStyle: dbData.titleStyle || presetDefaults.titleStyle || { color: '#ffffff', fontSize: 'text-4xl md:text-5xl', fontWeight: 'font-black', fontFamily: 'font-sans' },
        subtitleStyle: dbData.subtitleStyle || presetDefaults.subtitleStyle || { color: '#ffffff', fontSize: 'text-lg', fontWeight: 'font-normal', fontFamily: 'font-sans' },
        buttonText: dbData.buttonText || presetDefaults.buttonText || '',
        buttonBg: dbData.buttonBg || presetDefaults.buttonBg || '',
        buttonTextColor: dbData.buttonTextColor || presetDefaults.buttonTextColor || '',
        buttonStyle: dbData.buttonStyle || presetDefaults.buttonStyle || { fontSize: 'text-lg', fontWeight: 'font-bold', padding: 'py-4 px-8', borderRadius: 'rounded-xl' },
        imageUrl: dbData.imageUrl || '',
        showCountdown: dbData.showCountdown ?? presetDefaults.showCountdown ?? false,
        countdownValues: dbData.countdownValues || presetDefaults.countdownValues || { days: '02', hours: '14', mins: '35' },
        countdownBoxStyle: dbData.countdownBoxStyle || presetDefaults.countdownBoxStyle || { bg: 'bg-white/20', textColor: '#ffffff', borderRadius: 'rounded-lg' },
        badgeText: dbData.badgeText || presetDefaults.badgeText || '',
        badgeColor: dbData.badgeColor || presetDefaults.badgeColor || '',
        badgeStyle: dbData.badgeStyle || presetDefaults.badgeStyle || { fontSize: 'text-sm', padding: 'px-4 py-1.5', borderRadius: 'rounded-full' },
        features: dbData.features || presetDefaults.features || [],
        featureIconColor: dbData.featureIconColor || presetDefaults.featureIconColor || '#ffffff',
        featureTextColor: dbData.featureTextColor || presetDefaults.featureTextColor || '#ffffff',
        featureBoxStyle: dbData.featureBoxStyle || presetDefaults.featureBoxStyle || { bg: 'bg-white/20', padding: 'p-1.5', borderRadius: 'rounded-full' },
        showSparkleBadge: dbData.showSparkleBadge ?? presetDefaults.showSparkleBadge ?? true,
        sparkleBadgeText: dbData.sparkleBadgeText || presetDefaults.sparkleBadgeText || 'Featured',
        sparkleBadgeStyle: dbData.sparkleBadgeStyle || presetDefaults.sparkleBadgeStyle || { bg: 'bg-white/20', textColor: '#ffffff', padding: 'px-4 py-1.5', borderRadius: 'rounded-full' },
        showBenefits: dbData.showBenefits ?? presetDefaults.showBenefits ?? true,
        benefitsBoxStyle: dbData.benefitsBoxStyle || presetDefaults.benefitsBoxStyle || { bg: 'bg-white/10', padding: 'px-5 py-2.5', borderRadius: 'rounded-lg' },
        devMode: dbData.devMode || false,
        customStyles: {
          container: {
            ...config.customStyles.container,
            ...(dbData.customStyles?.container || {}),
          },
        },
      });
    };

    loadCtaSettings();
  }, [token, slug]);

  const saveCtaSettings = async () => {
    if (!token) {
      alert("Please log in to save");
      return;
    }

    try {
      setSaving(true);
      await axios.get(configs.API_URL + "/sanctum/csrf-cookie", { withCredentials: true });

      const xsrfToken = document.cookie
        .split('; ')
        .find(row => row.startsWith('XSRF-TOKEN='))
        ?.split('=')[1];

      const payload = { settings: config };

      const response = await axios.post(
        configs.API_URL + "/api/saveCtaSettings" + (slug ? `/${slug}` : ''),
        payload,
        {
          headers: {
            "Content-Type": "application/json",
            "Accept": "application/json",
            "X-XSRF-TOKEN": decodeURIComponent(xsrfToken || ''),
            Authorization: `Bearer ${token}`,
          },
          withCredentials: true,
        }
      );

      if (response.data.status === 1) {
        alert("CTA settings saved successfully! 🎉");
        if (onSave) onSave(config);
      } else {
        alert("Save failed: " + (response.data.message || "Unknown error"));
      }
    } catch (error) {
      console.error("Save error:", error);
      alert("Failed to save CTA settings");
    } finally {
      setSaving(false);
    }
  };

  const handleImageUpload = (e) => {
    const file = e.target.files[0];
    if (!file) return;

    const reader = new FileReader();
    reader.onload = () => {
      setConfig(prev => ({ ...prev, imageUrl: reader.result }));
    };
    reader.readAsDataURL(file);
  };

  const changePreset = (newPreset) => {
    const defaults = CTA_PRESETS[newPreset]?.defaultConfig || {};
    setConfig({
      ...config,
      preset: newPreset,
      ...defaults,
    });
  };

  const updateStyle = (styleKey, field, value) => {
    setConfig(prev => ({
      ...prev,
      [styleKey]: { ...prev[styleKey], [field]: value },
    }));
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-100 flex items-center justify-center">
        <p className="text-xl text-gray-600">Loading CTA settings...</p>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-100 flex">

      {/* Preview Area */}
      <div className="flex-1 relative">
        <div className="absolute inset-0 flex flex-col">
          <div className="bg-white border-b px-6 py-4 flex justify-between items-center shadow-sm z-10">
            <h1 className="text-2xl font-bold text-gray-900">CTA Block Editor</h1>
            <button
              onClick={() => setFullScreenPreview(true)}
              className="bg-indigo-600 text-white px-6 py-3 rounded-lg font-medium hover:bg-indigo-700 shadow transform hover:scale-105 transition"
            >
              Full Screen Preview
            </button>
          </div>
          <div className="flex-1 overflow-auto bg-gradient-to-b from-gray-50 to-gray-100 p-8">
            <div className="max-w-5xl mx-auto">
              <CTABlock config={config} />
            </div>
          </div>
        </div>
      </div>

      {/* Settings Sidebar */}
      <div className="w-96 bg-white border-l shadow-2xl overflow-y-auto">
        <div className="p-6 space-y-6">

          <h2 className="text-2xl font-bold text-gray-900">CTA Block Settings</h2>

          {/* Visibility */}
          <div className="flex items-center justify-between bg-indigo-50 p-4 rounded-xl">
            <span className="font-medium">Show this CTA block</span>
            <input
              type="checkbox"
              checked={config.isVisible}
              onChange={e => setConfig(prev => ({ ...prev, isVisible: e.target.checked }))}
              className="h-6 w-6 text-indigo-600 rounded"
            />
          </div>

          {/* Preset */}
          <div>
            <label className="block font-medium mb-2">Design Preset</label>
            <select
              value={config.preset}
              onChange={e => changePreset(e.target.value)}
              className="w-full border rounded-lg px-4 py-3"
            >
              {Object.entries(CTA_PRESETS).map(([key, p]) => (
                <option key={key} value={key}>{p.label}</option>
              ))}
            </select>
            <p className="text-sm text-gray-500 mt-1">{CTA_PRESETS[config.preset]?.description}</p>
          </div>

          {/* Common Fields – fully rich */}
          <div className="space-y-5">
            <div>
              <label className="block text-sm font-medium mb-1">Title</label>
              <input
                value={config.title}
                onChange={e => setConfig(prev => ({ ...prev, title: e.target.value }))}
                className="w-full border rounded-lg px-4 py-3"
              />
            </div>

            <div className="space-y-3">
              <h4 className="font-medium text-sm">Title Styling</h4>
              <input
                type="color"
                value={config.titleStyle.color}
                onChange={e => updateStyle('titleStyle', 'color', e.target.value)}
                className="h-10 w-full rounded border"
              />
              <select
                value={config.titleStyle.fontSize}
                onChange={e => updateStyle('titleStyle', 'fontSize', e.target.value)}
                className="w-full border rounded-lg px-4 py-3"
              >
                <option value="text-3xl md:text-4xl">3XL</option>
                <option value="text-4xl md:text-5xl">4XL</option>
                <option value="text-5xl md:text-6xl">5XL</option>
              </select>
              <select
                value={config.titleStyle.fontWeight}
                onChange={e => updateStyle('titleStyle', 'fontWeight', e.target.value)}
                className="w-full border rounded-lg px-4 py-3"
              >
                <option value="font-normal">Normal</option>
                <option value="font-bold">Bold</option>
                <option value="font-black">Black</option>
              </select>
            </div>

            <div>
              <label className="block text-sm font-medium mb-1">Subtitle</label>
              <textarea
                value={config.subtitle}
                onChange={e => setConfig(prev => ({ ...prev, subtitle: e.target.value }))}
                className="w-full border rounded-lg px-4 py-3 min-h-[80px]"
              />
            </div>

            <div className="space-y-3">
              <h4 className="font-medium text-sm">Subtitle Styling</h4>
              <input
                type="color"
                value={config.subtitleStyle.color}
                onChange={e => updateStyle('subtitleStyle', 'color', e.target.value)}
                className="h-10 w-full rounded border"
              />
              <select
                value={config.subtitleStyle.fontSize}
                onChange={e => updateStyle('subtitleStyle', 'fontSize', e.target.value)}
                className="w-full border rounded-lg px-4 py-3"
              >
                <option value="text-base">Base</option>
                <option value="text-lg">Large</option>
              </select>
            </div>

            <div>
              <label className="block text-sm font-medium mb-1">Text Color</label>
              <input
                type="color"
                value={config.textColor}
                onChange={e => setConfig(prev => ({ ...prev, textColor: e.target.value }))}
                className="h-10 w-full rounded border"
              />
            </div>

            <div>
              <label className="block text-sm font-medium mb-1">Background</label>
              <input
                value={config.background}
                onChange={e => setConfig(prev => ({ ...prev, background: e.target.value }))}
                className="w-full border rounded-lg px-4 py-3 font-mono text-sm"
              />
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium mb-1">Button Text</label>
                <input
                  value={config.buttonText}
                  onChange={e => setConfig(prev => ({ ...prev, buttonText: e.target.value }))}
                  className="w-full border rounded-lg px-4 py-3"
                />
              </div>
              <div>
                <label className="block text-sm font-medium mb-1">Button Background</label>
                <input
                  value={config.buttonBg}
                  onChange={e => setConfig(prev => ({ ...prev, buttonBg: e.target.value }))}
                  className="w-full border rounded-lg px-4 py-3 font-mono text-sm"
                />
              </div>
            </div>

            <div>
              <label className="block text-sm font-medium mb-1">Button Text Color</label>
              <input
                type="color"
                value={config.buttonTextColor}
                onChange={e => setConfig(prev => ({ ...prev, buttonTextColor: e.target.value }))}
                className="h-10 w-full rounded border"
              />
            </div>

            <div className="space-y-3">
              <h4 className="font-medium text-sm">Button Styling</h4>
              <select
                value={config.buttonStyle.fontSize}
                onChange={e => updateStyle('buttonStyle', 'fontSize', e.target.value)}
                className="w-full border rounded-lg px-4 py-3"
              >
                <option value="text-sm">Small</option>
                <option value="text-base">Base</option>
                <option value="text-lg">Large</option>
              </select>
              <input
                value={config.buttonStyle.padding}
                onChange={e => updateStyle('buttonStyle', 'padding', e.target.value)}
                className="w-full border rounded-lg px-4 py-3"
                placeholder="py-4 px-8"
              />
              <input
                value={config.buttonStyle.borderRadius}
                onChange={e => updateStyle('buttonStyle', 'borderRadius', e.target.value)}
                className="w-full border rounded-lg px-4 py-3"
                placeholder="rounded-xl"
              />
            </div>
          </div>

          {/* Image Upload */}
          <div>
            <label className="block text-sm font-medium mb-1">Background Image</label>
            <input
              type="file"
              accept="image/*"
              onChange={handleImageUpload}
              className="w-full text-sm text-gray-500 file:mr-4 file:py-2 file:px-4 file:rounded-lg file:border-0 file:text-sm file:font-semibold file:bg-indigo-50 file:text-indigo-700 hover:file:bg-indigo-100"
            />
            {config.imageUrl && (
              <img src={config.imageUrl} alt="preview" className="mt-3 w-full h-48 object-cover rounded-lg border" />
            )}
          </div>

          {/* Preset-specific – Split Urgency */}
          {config.preset === 'split-urgency' && (
            <div className="space-y-5 pt-6 border-t">
              <h4 className="font-medium text-gray-800">Urgency & Features</h4>

              <div className="flex items-center gap-3">
                <input
                  type="checkbox"
                  checked={config.showCountdown}
                  onChange={e => setConfig(prev => ({ ...prev, showCountdown: e.target.checked }))}
                  className="h-5 w-5"
                />
                <span className="font-medium">Show Countdown</span>
              </div>

              {config.showCountdown && (
                <div className="space-y-3">
                  <h5 className="text-sm font-medium">Countdown Box Style</h5>
                  <input
                    value={config.countdownBoxStyle.bg}
                    onChange={e => updateStyle('countdownBoxStyle', 'bg', e.target.value)}
                    className="w-full border rounded px-3 py-2 font-mono text-sm"
                    placeholder="bg-white/20"
                  />
                  <input
                    type="color"
                    value={config.countdownBoxStyle.textColor}
                    onChange={e => updateStyle('countdownBoxStyle', 'textColor', e.target.value)}
                    className="h-10 w-full rounded border"
                  />
                </div>
              )}

              <div className="space-y-3">
                <h5 className="text-sm font-medium">Badge</h5>
                <input
                  value={config.badgeText}
                  onChange={e => setConfig(prev => ({ ...prev, badgeText: e.target.value }))}
                  className="w-full border rounded-lg px-4 py-3"
                />
                <input
                  value={config.badgeColor}
                  onChange={e => setConfig(prev => ({ ...prev, badgeColor: e.target.value }))}
                  className="w-full border rounded-lg px-4 py-3 font-mono text-sm"
                />
              </div>

              <div className="space-y-4">
                <h5 className="text-sm font-medium">Features ({config.features.length})</h5>
                <button onClick={() => setConfig(prev => ({ ...prev, features: [...prev.features, 'New Feature'] }))} className="text-indigo-600 hover:underline text-sm">
                  + Add Feature
                </button>
                {config.features.map((f, i) => (
                  <div key={i} className="flex gap-2">
                    <input
                      value={f}
                      onChange={e => {
                        const newFeatures = [...config.features];
                        newFeatures[i] = e.target.value;
                        setConfig(prev => ({ ...prev, features: newFeatures }));
                      }}
                      className="flex-1 border rounded px-3 py-2"
                    />
                    <button onClick={() => setConfig(prev => ({ ...prev, features: prev.features.filter((_, idx) => idx !== i) }))} className="text-red-600">
                      ×
                    </button>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Dev Mode */}
          <details className="bg-yellow-50 border border-yellow-200 rounded-xl p-5">
            <summary className="font-semibold text-yellow-900 cursor-pointer">Dev Mode – Container</summary>
            <div className="mt-4 space-y-3">
              <input
                value={config.customStyles.container.padding}
                onChange={e => updateStyle('customStyles.container', 'padding', e.target.value)}
                className="w-full px-3 py-2 border rounded-md text-sm"
                placeholder="p-8 md:p-12"
              />
              <input
                value={config.customStyles.container.margin}
                onChange={e => updateStyle('customStyles.container', 'margin', e.target.value)}
                className="w-full px-3 py-2 border rounded-md text-sm"
                placeholder="mx-auto max-w-7xl"
              />
              <input
                value={config.customStyles.container.borderRadius}
                onChange={e => updateStyle('customStyles.container', 'borderRadius', e.target.value)}
                className="w-full px-3 py-2 border rounded-md text-sm"
                placeholder="rounded-2xl"
              />
              <input
                value={config.customStyles.container.boxShadow}
                onChange={e => updateStyle('customStyles.container', 'boxShadow', e.target.value)}
                className="w-full px-3 py-2 border rounded-md text-sm"
                placeholder="shadow-2xl"
              />
            </div>
          </details>

          {/* Save */}
          <div className="sticky bottom-0 bg-white border-t p-6 shadow-lg -mx-6">
            <button
              onClick={saveCtaSettings}
              disabled={saving}
              className={`w-full py-4 rounded-xl font-bold text-lg shadow-lg transition-all ${
                saving ? 'bg-gray-400 cursor-not-allowed' : 'bg-indigo-600 hover:bg-indigo-700 text-white hover:shadow-xl'
              }`}
            >
              {saving ? 'Saving...' : 'Save & Publish CTA'}
            </button>
          </div>
        </div>
      </div>

      {/* Full Screen Preview */}
      {fullScreenPreview && (
        <div className="fixed inset-0 bg-black/95 z-50 flex flex-col" onClick={() => setFullScreenPreview(false)}>
          <div className="bg-white/10 backdrop-blur px-6 py-4 flex justify-end">
            <button className="text-white text-4xl hover:text-gray-300">×</button>
          </div>
          <div className="flex-1 overflow-auto p-8" onClick={e => e.stopPropagation()}>
            <CTABlock config={config} />
          </div>
        </div>
      )}
    </div>
  );
};

export default CTABlockAdmin;